import { Button, Stack, Text } from "@mantine/core";
import { NavigationProgress } from "@mantine/nprogress";
import { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";
import { bootstrapSession } from "@/lib/session";
import { useAccess } from "@/hooks/useAccess";
import { BootLoader } from "@/components/BootLoader";
import { landingPageFor, PAGE_AREAS } from "@/lib/permissions";
import AppLayout from "@/layouts/AppLayout";
import Login from "@/pages/Login";
import Invite from "@/pages/Invite";
import Dashboard from "@/pages/Dashboard";
import Categories from "@/pages/Categories/Categories";
import Products from "@/pages/Products/Products";
import Branches from "@/pages/Branches/Branches";
import SupplyRequests from "@/pages/Supply/SupplyRequests";
import SupplyItems from "@/pages/Supply/SupplyItems";
import Connections from "@/pages/Connections/Connections";
import Mappings from "@/pages/Connections/Mappings";
import Bulk from "@/pages/Bulk/Bulk";
import BrandSettings from "@/pages/Brand/BrandSettings";
import Users from "@/pages/Users/Users";
import Tenants from "@/pages/Admin/Tenants";

// Yetki çözülemedi ya da kullanıcının açabileceği hiçbir sayfa yok. İkisi de sessiz boş ekranla
// bitiyordu; burada en azından ne olduğu ve ne yapılacağı yazıyor.
function AccessError({ onRetry }) {
  return (
    <Stack align="center" gap="sm" py="xl">
      <Text fw={600}>
        {onRetry
          ? "Yetkilerin alınamadı"
          : "Görüntüleyebileceğin bir sayfa yok"}
      </Text>
      <Text size="sm" c="dimmed" ta="center">
        {onRetry
          ? "Bağlantı kurulamadı. Tekrar deneyebilir ya da sayfayı yenileyebilirsin."
          : "Hesabına henüz yetki tanımlanmamış. Marka yöneticinle iletişime geç."}
      </Text>
      {onRetry && (
        <Button variant="light" onClick={() => onRetry()}>
          Tekrar dene
        </Button>
      )}
    </Stack>
  );
}

function ProtectedRoute({ children }) {
  const token = useAuthStore((state) => state.token);
  return token ? children : <Navigate to="/login" replace />;
}

// Access token bellekte durduğu için tam sayfa yenilemesinde kayboluyor; httpOnly refresh çerezi
// varsa oturum geri geliyor. Bu deneme bitmeden yönlendirme YAPILMIYOR — yoksa oturumu açık olan
// kullanıcı her F5'te bir an giriş ekranına düşerdi.
function SessionGate({ children }) {
  const status = useAuthStore((state) => state.status);

  useEffect(() => {
    bootstrapSession();
  }, []);

  if (status === "loading") {
    return <BootLoader />;
  }

  return children;
}

function SuperadminRoute({ children }) {
  const user = useAuthStore((state) => state.user);
  return user?.role === "superadmin" ? children : <Navigate to="/" replace />;
}

// Menüden gizlemek yetmez: adres çubuğuna elle yazan ya da eski bir link'i açan kullanıcı da
// yetkisiz sayfayı görmemeli. /me henüz gelmeden yönlendirme yapılmıyor — aksi halde her tam
// sayfa yenilemesinde yetkili kullanıcı bir an dashboard'a atılırdı.
function RequirePermission({ path, children }) {
  const { canAny, loaded, failed, retry } = useAccess();
  const user = useAuthStore((state) => state.user);
  const impersonation = useAuthStore((state) => state.impersonation);

  // Superadmin'in kiracı sayfalarında işi yok: her sorgusu API'de 403 dönüyor. Kendi alanına
  // gönderiliyor, yoksa sayfa açılıp hata kaydı üretiyordu.
  if (user?.role === "superadmin" && !impersonation) {
    return <Navigate to="/admin/tenants" replace />;
  }

  // /me düşerse loaded hiç true olmaz; boş ekranla bırakmak yerine sebebini ve tekrar deneme
  // yolunu göster.
  if (failed) {
    return <AccessError onRetry={retry} />;
  }

  if (!loaded) {
    return null;
  }

  const required = PAGE_AREAS[path];
  // Haritada karşılığı olmayan yol korumasız demektir; açık bırakmak yerine kapat. Yeni bir sayfa
  // eklenip PAGE_AREAS'a yazılmadığında sessizce herkese açılmasın.
  const allowed = Boolean(required) && canAny(required);

  if (allowed) {
    return children;
  }

  const landing = landingPageFor(canAny);

  return !landing || landing === path ? (
    <AccessError />
  ) : (
    <Navigate to={landing} replace />
  );
}

// Superadmin without impersonation has no tenant scope; land on the admin section
// instead of tenant pages that would 403.
function Home() {
  const user = useAuthStore((state) => state.user);
  const impersonation = useAuthStore((state) => state.impersonation);

  if (user?.role === "superadmin" && !impersonation) {
    return <Navigate to="/admin/tenants" replace />;
  }

  return (
    <RequirePermission path="/">
      <Dashboard />
    </RequirePermission>
  );
}

export default function App() {
  return (
    <SessionGate>
      {/* Yalnızca YAZMA istekleri için üstte ince çubuk (bkz. lib/axios) — sayfa geçişlerinde
                çıkmıyor. Bloke etmiyor; ekranı kilitleyen tek yer açılış (SessionGate), orada
                gidilecek sayfa henüz belli değil. */}
      <NavigationProgress color="chocolate" size={3} />
      <Routes>
        <Route path="/login" element={<Login />} />
        {/* Davet bağlantısı oturum İSTEMEZ: buraya gelen kişinin henüz şifresi yok.
                    ProtectedRoute'un içine koymak, davetliyi giriş ekranına atardı. */}
        <Route path="/davet/:token" element={<Invite />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Home />} />
          <Route
            path="categories"
            element={
              <RequirePermission path="/categories">
                <Categories />
              </RequirePermission>
            }
          />
          <Route
            path="products"
            element={
              <RequirePermission path="/products">
                <Products />
              </RequirePermission>
            }
          />
          <Route
            path="branches"
            element={
              <RequirePermission path="/branches">
                <Branches />
              </RequirePermission>
            }
          />
          <Route
            path="connections"
            element={
              <RequirePermission path="/connections">
                <Connections />
              </RequirePermission>
            }
          />
          {/* Eşleştirme ekranı entegrasyon sayfasının devamı — aynı izinle korunur. */}
          <Route
            path="connections/:id/mappings"
            element={
              <RequirePermission path="/connections">
                <Mappings />
              </RequirePermission>
            }
          />
          <Route
            path="bulk"
            element={
              <RequirePermission path="/bulk">
                <Bulk />
              </RequirePermission>
            }
          />
          <Route
            path="settings"
            element={
              <RequirePermission path="/settings">
                <BrandSettings />
              </RequirePermission>
            }
          />
          <Route
            path="supply"
            element={
              <RequirePermission path="/supply">
                <SupplyRequests />
              </RequirePermission>
            }
          />
          <Route
            path="supply/items"
            element={
              <RequirePermission path="/supply/items">
                <SupplyItems />
              </RequirePermission>
            }
          />
          <Route
            path="users"
            element={
              <RequirePermission path="/users">
                <Users />
              </RequirePermission>
            }
          />
          <Route
            path="admin/tenants"
            element={
              <SuperadminRoute>
                <Tenants />
              </SuperadminRoute>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </SessionGate>
  );
}
